import {defineStore} from 'pinia';
import {CommunityIdentificationType, CommunityType, LegalEntityType} from '../../types/src/enums';
import type {IAddressPostal, IEnergyCommunity} from '../../types/src/schemas';
import type {FormEnergyCommunity} from './communityApiStore';
import {useCommunityApiStore} from './communityApiStore';

/**
 * Form-compatible version of IAddressPostal for backward compatibility
 * TODO esb-452: Update to use IAddressPostal directly
 */
interface CommunityAddressPostal {
  name: string;
  address: string;
  addressExtra: string;
  zip: string;
  city: string;
}

/**
 * Last selected community information (persisted)
 */
interface LastCommunityInfo {
  name: string;
  logoSrc: string;
  uriMoreInfo: string;
}

/**
 * State for the community data store
 */
interface CommunityDataState {
  communitySelected: FormEnergyCommunity | null;
  communitiesAvailable: FormEnergyCommunity[];
  lastCommunity: LastCommunityInfo;
  loading: boolean;
  error: string | null;
}

/**
 * Convert form address format to IAddressPostal
 */
function convertToAddressPostal(address: CommunityAddressPostal): IAddressPostal {
  return {
    street: address.address,
    streetNumber: "", // TODO esb-452: Parse from address
    additional: address.addressExtra,
    zip: address.zip,
    city: address.city,
    country: "AT" // Default to Austria
  };
}

/**
 * Convert FormEnergyCommunity to IEnergyCommunity
 */
function convertToEnergyCommunity(community: FormEnergyCommunity): IEnergyCommunity {
  return {
    ...community,
    communityType: community.communityType as CommunityType,
    legal: community.legal as LegalEntityType,
    identificationType: community.identificationType as CommunityIdentificationType,
    addressPostal: convertToAddressPostal(community.addressPostal)
  };
}

export const useCommunityDataPublic = defineStore('communityDataPublic', {
  persist: {
    storage: sessionStorage,
    paths: ['lastCommunity.name', 'lastCommunity.logoSrc', 'lastCommunity.uriMoreInfo'],
  },

  state: (): CommunityDataState => ({
    communitySelected: null,
    lastCommunity: {
      name: '',
      logoSrc: '',
      uriMoreInfo: ''
    },
    communitiesAvailable: [],
    loading: false,
    error: null
  }),

  getters: {
    communityActive: (state): FormEnergyCommunity | null => state.communitySelected,
    communitiesList: (state): FormEnergyCommunity[] => state.communitiesAvailable,
    getLastCommunity: (state): LastCommunityInfo => state.lastCommunity,

    // New getter that converts to the standard shared type
    communityActiveStandard: (state): IEnergyCommunity | null => {
      if (!state.communitySelected) return null;
      return convertToEnergyCommunity(state.communitySelected);
    },

    // New getter that provides properly typed community list
    communitiesListStandard: (state): IEnergyCommunity[] => {
      return state.communitiesAvailable.map(convertToEnergyCommunity);
    }
  },

  actions: {
    /**
     * Set the active community and update last selected community
     */
    setCommunityActive(community: FormEnergyCommunity | null): void {
      if (community === null) {
        // Explicitly setting to null - this is valid (no community selected)
        this.communitySelected = null;
      } else if (community && typeof community === 'object') {
        // Valid community object provided
        this.communitySelected = community;

        this.lastCommunity = {
          name: community.name,
          logoSrc: community.logoSrc,
          uriMoreInfo: community.uriMoreInfo
        };
      } else {
        // Invalid value provided (not null and not a valid object)
        console.error('Invalid community object provided:', community);
        this.communitySelected = null;
      }
    },

    /**
     * Fetch all communities from the API
     */
    async fetchCommunities(): Promise<void> {
      this.loading = true;
      this.error = null;
      const communityApiStore = useCommunityApiStore();

      try {
        const communities = await communityApiStore.fetchCommunities();

        if (communities && Array.isArray(communities)) {
          this.communitiesAvailable = communities;
        }
      } catch (error) {
        console.error('Error fetching communities:', error);
        this.error = error instanceof Error ? error.message : 'Failed to fetch communities';
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch a specific community by ECN
     */
    async fetchCommunityByEcn(ecn: string): Promise<FormEnergyCommunity | null> {
      this.loading = true;
      this.error = null;
      const communityApiStore = useCommunityApiStore();

      try {
        return await communityApiStore.fetchCommunityByEcn(ecn);
      } catch (error) {
        console.error(`Error fetching community with ECN ${ecn}:`, error);
        this.error = error instanceof Error ? error.message : `Failed to fetch community with ECN ${ecn}`;
        return null;
      } finally {
        this.loading = false;
      }
    }
  }
});
